import {
  ProjectPriority,
  ProjectPriorityByValue,
  SaveTaskDto,
  Task,
  TaskLevel,
  TaskStatus,
} from '../../../../app/entities';
import type { GroupByOption } from '../../task-grouping-options';

/**
 * A single board column. `id` is the stable key used by dnd-kit (also the key
 * in the `Record<columnId, taskId[]>` board state). `value` is the underlying
 * field value used to build the persistence patch (`null` for the "No X"
 * buckets). `name` is the column header label.
 */
export type TaskColumn = {
  id: string;
  value: any;
  name: string;
};

/**
 * Context needed to enumerate the full column set for a grouping. The board
 * passes the current project and/or space; only the fields relevant to the
 * active grouping are read.
 */
export type TaskColumnContext = {
  project?: { steps?: { _id: string; name?: string }[]; collaborators?: { _id: string; fullName: string }[] };
  space?: {
    members?: { _id: string; fullName: string }[];
    projects?: { _id: string; name: string }[];
    tags?: { _id: string; text?: string; target?: string }[];
  };
};

const STATUS_ORDER: TaskStatus[] = [
  TaskStatus.Todo,
  TaskStatus.InProgress,
  TaskStatus.OnHold,
  TaskStatus.Done,
  TaskStatus.Canceled,
];

const PRIORITY_ORDER: ProjectPriority[] = [
  ProjectPriority.NO_PRIORITY,
  ProjectPriority.LOW,
  ProjectPriority.MEDIUM,
  ProjectPriority.HIGH,
  ProjectPriority.URGENT,
];

export const NO_ASSIGNEE_ID = 'no-assignee';
export const NO_STEP_ID = 'no-step';
export const NO_PROJECT_ID = 'no-project';
export const NO_TAG_ID = 'no-tag';

/**
 * Enumerate the complete, ordered set of columns for the active grouping —
 * including empty ones — so a card can be dropped into a column that currently
 * holds no tasks. This is the key difference from the list view, which only
 * renders non-empty groups.
 */
export function getTaskColumns(
  groupBy: GroupByOption,
  taskLevel: TaskLevel,
  ctx: TaskColumnContext,
): TaskColumn[] {
  switch (groupBy.key) {
    case 'status':
      return STATUS_ORDER.map((status) => ({
        id: status,
        value: status,
        name: Task.StatusMapper[status].title,
      }));

    case 'priority':
      return PRIORITY_ORDER.map((priority) => ({
        id: String(priority),
        value: priority,
        name: ProjectPriorityByValue[priority],
      }));

    case 'assigneeId': {
      const members =
        taskLevel === 'space' ? ctx.space?.members ?? [] : ctx.project?.collaborators ?? [];

      const columns = members
        .map((member) => ({ id: member._id, value: member._id, name: member.fullName }))
        .sort((a, b) => a.name.localeCompare(b.name));

      columns.push({ id: NO_ASSIGNEE_ID, value: null, name: 'No assignee' });
      return columns;
    }

    case 'stepId': {
      const steps = ctx.project?.steps ?? [];

      const columns = steps.map((step) => ({
        id: step._id,
        value: step._id,
        name: step.name ?? 'Untitled step',
      }));

      columns.push({ id: NO_STEP_ID, value: null, name: 'No step' });
      return columns;
    }

    case 'projectId': {
      const projects = ctx.space?.projects ?? [];

      const columns = projects
        .map((project) => ({ id: project._id, value: project._id, name: project.name }))
        .sort((a, b) => a.name.localeCompare(b.name));

      columns.push({ id: NO_PROJECT_ID, value: null, name: 'No project' });
      return columns;
    }

    case 'tag': {
      const tags = (ctx.space?.tags ?? []).filter((tag) => tag.target === 'task');

      const columns = tags
        .map((tag) => ({ id: tag._id, value: tag._id, name: tag.text ?? 'Untitled tag' }))
        .sort((a, b) => a.name.localeCompare(b.name));

      columns.push({ id: NO_TAG_ID, value: null, name: 'No tag' });
      return columns;
    }

    default:
      return [];
  }
}

/**
 * Returns the column id(s) a task belongs to for the active grouping. Every
 * grouping yields exactly one id except `tag`, where a task can appear in
 * several tag columns (tag grouping is read-only on the board).
 */
export function getTaskColumnIds(task: Task, groupByKey: string): string[] {
  switch (groupByKey) {
    case 'status':
      return [task.status];
    case 'priority':
      return [String(task.priority ?? ProjectPriority.NO_PRIORITY)];
    case 'assigneeId':
      return [task.assigneeId || NO_ASSIGNEE_ID];
    case 'stepId':
      return [task.stepId || NO_STEP_ID];
    case 'projectId':
      return [task.projectId || NO_PROJECT_ID];
    case 'tag':
      return task.tagsIds.length ? [...task.tagsIds] : [NO_TAG_ID];
    default:
      return [];
  }
}

/**
 * Build the `Record<columnId, taskId[]>` board state. Every column gets an
 * entry (so empty columns remain droppable). A task referencing a column that
 * isn't enumerated (e.g. an assignee no longer on the project) falls back to
 * the grouping's "No X" bucket so it is never silently dropped.
 */
export function bucketTasksIntoColumns(
  columns: TaskColumn[],
  tasks: Task[],
  groupByKey: string,
): Record<string, string[]> {
  const items: Record<string, string[]> = {};
  for (const column of columns) {
    items[column.id] = [];
  }

  const fallbackId = columns.find((column) => column.value === null)?.id;

  for (const task of tasks) {
    for (const columnId of getTaskColumnIds(task, groupByKey)) {
      if (items[columnId]) {
        items[columnId].push(task._id);
      } else if (fallbackId) {
        items[fallbackId].push(task._id);
      }
    }
  }

  return items;
}

/**
 * Build the `SaveTaskDto` patch that moves a task into the target column.
 * Dropping into a "No X" bucket (value `null`) clears the grouped field;
 * otherwise the grouping's own `getDefaultValue` produces the patch.
 */
export function getColumnMovePatch(
  groupBy: GroupByOption,
  column: TaskColumn,
): Partial<SaveTaskDto> {
  if (column.value === null || column.value === undefined) {
    return { [groupBy.key]: null } as Partial<SaveTaskDto>;
  }

  return groupBy.getDefaultValue(column.value);
}
